import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const navLinks = [
  { label: 'Features', to: '/#features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Editor', to: '/editor' },
  { label: 'About', to: '/about' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
  };

  const emailInitial = user?.email?.[0]?.toUpperCase() ?? 'U';

  return (
    <header className="sticky top-0 z-50 h-[60px] flex items-center bg-bg-elevated/80 backdrop-blur-md border-b border-border-subtle">
      <div className="w-full max-w-6xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 text-text-primary font-semibold text-lg">
          <img src="/favicon.svg" alt="RSP" className="w-7 h-7" />
          <span>RSP</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors duration-fast ${
                location.pathname === link.to
                  ? 'text-brand'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 w-9 h-9 rounded-full bg-brand text-white text-sm font-semibold"
              >
                {emailInitial}
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-bg-surface border border-border-subtle rounded-radius-lg shadow-lg py-1">
                  <div className="px-4 py-2 border-b border-border-subtle">
                    <p className="text-sm font-medium text-text-primary truncate">{user.email}</p>
                    <p className="text-xs text-text-muted capitalize">{user.plan} plan</p>
                  </div>
                  <Link
                    to="/editor"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                  >
                    <User size={14} /> Editor
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/auth">
                <Button variant="primary" size="sm">Get Started Free</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-text-secondary hover:text-text-primary transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden absolute top-[60px] left-0 right-0 bg-bg-overlay border-b border-border-subtle shadow-lg">
          <nav className="flex flex-col p-4 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="h-12 flex items-center px-4 text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-md transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-3 px-4">
              {user ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary">
                    <div className="w-8 h-8 rounded-full bg-brand text-white text-sm font-semibold flex items-center justify-center">
                      {emailInitial}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-text-primary font-medium">{user.email}</p>
                      <p className="text-xs text-text-muted capitalize">{user.plan} plan</p>
                    </div>
                  </div>
                  <Link to="/editor" onClick={() => setMobileOpen(false)}>
                    <Button variant="secondary" size="md" className="w-full">Editor</Button>
                  </Link>
                  <Button variant="ghost" size="md" className="w-full" onClick={() => { setMobileOpen(false); handleLogout(); }}>
                    Sign out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" size="md" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/auth" onClick={() => setMobileOpen(false)}>
                    <Button variant="primary" size="md" className="w-full">Get Started Free</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}